import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { MdClose } from "react-icons/md";
import { FiAlertTriangle, FiPackage } from "react-icons/fi";
import { useClickOutside } from "./../../Hooks/useClickOutside";

const RaiseReturnDisputeModal = ({
  open,
  onClose,
  onConfirm,   // ({ returnType, reason, partialReturnPackets? }) => Promise<void>
  loading = false,
  quotationUniqueNumber = "",
}) => {
  const [animate, setAnimate] = useState(false);
  const [returnType, setReturnType] = useState(null);   // "Full_Order" | "Partial"
  const [reason, setReason] = useState("");
  const [packets, setPackets] = useState("");

  // ── animation + reset ─────────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      setTimeout(() => setAnimate(true), 10);
    } else {
      setAnimate(false);
      setReturnType(null);
      setReason("");
      setPackets("");
    }
  }, [open]);

  const handleClose = () => {
    if (loading) return;
    setAnimate(false);
    setTimeout(onClose, 250);
  };

  const domNode = useClickOutside(() => handleClose());

  const isPartial = returnType === "Partial";
  const packetsNum = parseInt(packets, 10);

  const canSubmit =
    returnType !== null &&
    reason.trim().length >= 10 &&
    (!isPartial || (packets !== "" && packetsNum >= 1));

  const handleSubmit = () => {
    if (!canSubmit || loading) return;
    onConfirm({
      returnType,
      reason: reason.trim(),
      ...(isPartial && {
        partialReturnPackets: packetsNum,
        packets: packetsNum,
      }),
    });
  };

  if (!open) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-end md:items-center justify-center
        bg-black/40 backdrop-blur-sm transition-opacity duration-300
        ${animate ? "opacity-100" : "opacity-0"}`}
    >
      <div
        ref={domNode}
        className={`w-full md:w-[540px] md:max-h-[90vh] overflow-y-auto bg-white shadow-2xl md:rounded-3xl rounded-t-3xl
          transition-all duration-300
          ${animate
            ? "scale-100 translate-y-0"
            : "scale-95 md:translate-y-4 translate-y-full"
          }`}
      >
        {/* ── HEADER ───────────────────────────────────────────────────────── */}
        <div className="flex items-center sticky top-0 z-10 justify-between px-6 py-4 border-b bg-gradient-to-r from-red-500 via-rose-500 to-red-600 md:rounded-t-3xl rounded-t-3xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <FiAlertTriangle className="text-white" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">
                Raise Return Dispute
              </h2>
              <p className="text-xs text-red-100">
                {quotationUniqueNumber
                  ? `Quotation ${quotationUniqueNumber}`
                  : "Please fill in the details below"}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 bg-white rounded-full hover:bg-red-100 transition disabled:opacity-50"
          >
            <MdClose size={20} className="text-red-600" />
          </button>
        </div>

        {/* ── BODY ─────────────────────────────────────────────────────────── */}
        <div className="px-6 py-6 space-y-5">

          {/* ── STEP 1: Return type selector ─────────────────────────────── */}
          <div className="space-y-2">
            <p className="text-xs font-black text-gray-500 uppercase tracking-widest">
              What would you like to return?
              <span className="text-red-500 ml-0.5">*</span>
            </p>
            <div className="grid grid-cols-2 gap-3">
              {/* Full Order */}
              <button
                type="button"
                onClick={() => {
                  setReturnType("Full_Order");
                  setPackets(""); // reset packets if switching
                }}
                className={`relative flex flex-col items-center gap-2.5 p-4 rounded-2xl border-2
                  transition-all duration-200 text-left
                  ${returnType === "Full_Order"
                    ? "border-red-500 bg-red-50 shadow-md shadow-red-100"
                    : "border-gray-200 bg-gray-50 hover:border-gray-300"
                  }`}
              >
                {returnType === "Full_Order" && (
                  <span className="absolute top-2.5 right-2.5 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
                    <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                )}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center
                  ${returnType === "Full_Order" ? "bg-red-500" : "bg-gray-200"}`}
                >
                  <FiPackage
                    size={18}
                    className={returnType === "Full_Order" ? "text-white" : "text-gray-500"}
                  />
                </div>
                <div>
                  <p className={`text-sm font-bold ${returnType === "Full_Order" ? "text-red-700" : "text-gray-700"}`}>
                    Full Order
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">
                    Return entire order
                  </p>
                </div>
              </button>

              {/* Partial */}
              <button
                type="button"
                onClick={() => setReturnType("Partial")}
                className={`relative flex flex-col items-center gap-2.5 p-4 rounded-2xl border-2
                  transition-all duration-200 text-left
                  ${returnType === "Partial"
                    ? "border-amber-500 bg-amber-50 shadow-md shadow-amber-100"
                    : "border-gray-200 bg-gray-50 hover:border-gray-300"
                  }`}
              >
                {returnType === "Partial" && (
                  <span className="absolute top-2.5 right-2.5 w-4 h-4 rounded-full bg-amber-500 flex items-center justify-center">
                    <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                )}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center
                  ${returnType === "Partial" ? "bg-amber-500" : "bg-gray-200"}`}
                >
                  <FiPackage
                    size={18}
                    className={returnType === "Partial" ? "text-white" : "text-gray-500"}
                  />
                </div>
                <div>
                  <p className={`text-sm font-bold ${returnType === "Partial" ? "text-amber-700" : "text-gray-700"}`}>
                    Partial Return
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">
                    Return specific packets
                  </p>
                </div>
              </button>
            </div>
          </div>

          {/* ── STEP 2: Packets — only for Partial ───────────────────────── */}
          {isPartial && (
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">
                Number of Packets / Units to Return
                <span className="text-red-500 ml-0.5">*</span>
              </label>
              <input
                type="number"
                min={1}
                value={packets}
                onChange={(e) => setPackets(e.target.value)}
                disabled={loading}
                placeholder="e.g. 5"
                className="w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50
                  text-sm font-semibold text-gray-800 placeholder:text-gray-400
                  focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-400
                  disabled:opacity-50 transition"
              />
              {packets !== "" && packetsNum < 1 && (
                <p className="text-xs text-red-500 font-semibold">
                  Must be at least 1 packet
                </p>
              )}
            </div>
          )}

          {/* ── STEP 3: Reason ────────────────────────────────────────────── */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">
              Reason for Return
              <span className="text-red-500 ml-0.5">*</span>
            </label>
            <textarea
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading}
              placeholder="Describe the issue clearly — e.g. product quality, wrong item, damaged goods..."
              className="w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50
                text-sm text-gray-800 placeholder:text-gray-400 resize-none
                focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400
                disabled:opacity-50 transition"
            />
            <div className="flex items-center justify-between">
              {reason.trim().length > 0 && reason.trim().length < 10 ? (
                <p className="text-xs text-red-500 font-semibold">
                  Please enter at least 10 characters
                </p>
              ) : (
                <span />
              )}
              <p
                className={`text-xs ml-auto font-semibold
                  ${reason.trim().length >= 10 ? "text-green-500" : "text-gray-400"}`}
              >
                {reason.trim().length} chars
              </p>
            </div>
          </div>

          {/* ── Warning note ──────────────────────────────────────────────── */}
          <div className="flex items-start gap-2.5 p-3.5 rounded-2xl bg-amber-50 border border-amber-200">
            <FiAlertTriangle
              className="text-amber-500 flex-shrink-0 mt-0.5"
              size={14}
            />
            <p className="text-xs text-amber-700 leading-relaxed font-medium">
              Once submitted, this return dispute will be reviewed by our admin
              team. An OFM will be assigned to investigate your case on-site.
            </p>
          </div>
        </div>

        {/* ── FOOTER ───────────────────────────────────────────────────────── */}
        <div className="flex gap-3 px-6 py-5 border-t">
          <button
            onClick={handleClose}
            disabled={loading}
            className="flex-1 py-3.5 border-2 border-gray-300 text-gray-700 rounded-2xl
              font-medium hover:bg-gray-50 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || loading}
            className="flex-[2] py-3.5 bg-gradient-to-r from-red-500 to-rose-600
              text-white font-semibold rounded-2xl transition-all duration-300
              hover:shadow-[0px_6px_16px_rgba(239,68,68,0.4)]
              disabled:opacity-50 disabled:cursor-not-allowed
              flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    className="opacity-25"
                    cx="12" cy="12" r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                  />
                </svg>
                Submitting...
              </>
            ) : (
              <>⚠ Submit Return Dispute</>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RaiseReturnDisputeModal;